import { BENCH_ENVELOPE_BOUNDS, BENCH_SUBMISSION_MAX_BYTES, benchLabel, redactQuery, type BenchEnvelope, type BenchSubmission } from "odyssey-shared";
import { MASKED, UNAVAILABLE, type MachineField } from "./machine.ts";

/**
 * Sending a finished benchmark report to the dev server (P2.0-T8, R-0152), so every run is saved where the dev server
 * runs without anyone copying it. The page posts the report's text, exactly what "Copy results" copies, with a small
 * envelope to the endpoint the dev server's `config.json` names; a page with no such endpoint, which is every built
 * page, posts nothing. The machine label (`&label=<name>`: letters, digits, dash and underscore, at most 32) names the
 * saved file and is remembered in `localStorage` for the next run in the same browser. Nothing here throws: a post
 * that fails for any reason, a server that does not answer within ten seconds included, becomes a sentence the panel
 * shows, and the copy button works as before.
 */

/**
 * The `localStorage` key the machine label is remembered under.
 */
export const LABEL_KEY = "odyssey.bench.label";

/**
 * How long a post may take before it is given up, in milliseconds.
 */
export const POST_TIMEOUT_MS = 10_000;

const REASON_MAX = 160;
const SAVED_FILE = /^[A-Za-z0-9_-]{1,96}\.txt$/;
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/g;

/**
 * What the page knows of itself when a run ends.
 */
export interface PageFacts {
  readonly when: Date;
  /** `location.search`. */
  readonly search: string;
  readonly userAgent: string;
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly devicePixelRatio: number;
}

/**
 * How a post ended: the saved file's name, or why nothing was saved.
 */
export type SaveOutcome = { readonly file: string } | { readonly reason: string };

/**
 * A screen dimension within the schema's bounds.
 *
 * @param value What the browser reports.
 * @returns A whole number from 1 to the largest dimension; 1 for a value that is not a number.
 */
function dimension(value: number): number {
  return Number.isFinite(value) ? Math.min(BENCH_ENVELOPE_BOUNDS.screen, Math.max(1, Math.round(value))) : 1;
}

/**
 * A field of the report's machine block.
 *
 * @param machine The block.
 * @param key The field's label.
 * @returns Its value, or null when the block has no such field.
 */
function field(machine: readonly MachineField[], key: string): string | null {
  return machine.find((entry) => entry.key === key)?.value ?? null;
}

/**
 * Builds the envelope a report is posted with, every field cut or clamped to the schema's bounds so a browser that
 * reports something odd still has its run saved.
 *
 * @param page What the page knows of itself.
 * @param machine The report's machine block, for the renderer and the build it already read.
 * @param label What the panel's label box holds.
 * @returns The envelope: the query with a token's value blanked, the renderer null when the browser gave none, and
 *   the label null unless the label rule admits it.
 */
export function benchEnvelope(page: PageFacts, machine: readonly MachineField[], label: string): BenchEnvelope {
  const bounds = BENCH_ENVELOPE_BOUNDS;
  const renderer = field(machine, "GPU renderer");
  return {
    when: page.when.toISOString(),
    query: redactQuery(page.search).slice(0, bounds.query),
    userAgent: page.userAgent.slice(0, bounds.userAgent),
    screen: { width: dimension(page.screenWidth), height: dimension(page.screenHeight) },
    devicePixelRatio: page.devicePixelRatio > 0 ? Math.min(bounds.pixelRatio, page.devicePixelRatio) : 1,
    renderer: renderer === null || renderer === UNAVAILABLE || renderer === MASKED ? null : renderer.slice(0, bounds.renderer),
    build: (field(machine, "Build") ?? UNAVAILABLE).slice(0, bounds.build),
    label: benchLabel(label),
  };
}

/**
 * What the label box starts with.
 *
 * @param fromUrl The page's `?label=` value, or null.
 * @param stored The label this browser remembers, or null.
 * @returns The link's label when it has one, else the remembered label, else nothing.
 */
export function initialLabel(fromUrl: string | null, stored: string | null): string {
  return fromUrl !== null && fromUrl !== "" ? fromUrl : (benchLabel(stored) ?? "");
}

/**
 * The machine label this browser remembers.
 *
 * @param storage The page's `localStorage`, or null where there is none.
 * @returns The label, or null when none is kept, it no longer passes the label rule, or storage is blocked.
 */
export function storedLabel(storage: Pick<Storage, "getItem"> | null): string | null {
  try {
    return benchLabel(storage?.getItem(LABEL_KEY));
  } catch {
    return null;
  }
}

/**
 * Remembers a machine label for the next run in this browser. A label the rule does not admit, and a browser that
 * blocks storage, change nothing.
 *
 * @param storage The page's `localStorage`, or null where there is none.
 * @param label What the label box holds.
 */
export function keepLabel(storage: Pick<Storage, "setItem"> | null, label: string): void {
  const kept = benchLabel(label);
  if (kept === null) {
    return;
  }
  try {
    storage?.setItem(LABEL_KEY, kept);
  } catch {
    return;
  }
}

/**
 * A server's refusal as one short line: control characters dropped, white space collapsed, cut to a length.
 *
 * @param text The response's text.
 * @returns The line.
 */
function oneLine(text: string): string {
  return text.replace(/\s+/g, " ").replace(CONTROL, "").trim().slice(0, REASON_MAX);
}

/**
 * A signal that aborts after a time, and the call that clears its timer once the request has settled. It is the one
 * place this module makes a timeout signal, so a shared helper of the same purpose replaces it in one line.
 *
 * @param ms The time, in milliseconds.
 * @returns The signal, a promise that resolves when the time has passed, and `clear`, which stops the timer.
 */
function abortAfter(ms: number): { readonly signal: AbortSignal; readonly expired: Promise<void>; readonly clear: () => void } {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const expired = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve();
    }, ms);
  });
  return {
    signal: controller.signal,
    expired,
    clear: () => {
      if (timer !== null) {
        clearTimeout(timer);
      }
    },
  };
}

/**
 * Reads the answer to a post.
 *
 * @param response The response.
 * @returns The saved file's name, or the reason nothing was saved.
 * @throws Error when the body cannot be read.
 */
async function readAnswer(response: Response): Promise<SaveOutcome> {
  const text = await response.text();
  if (response.status !== 201) {
    return { reason: `${response.status} ${oneLine(text)}`.trim() };
  }
  let file: unknown;
  try {
    file = (JSON.parse(text) as { file?: unknown } | null)?.file;
  } catch {
    file = null;
  }
  return typeof file === "string" && SAVED_FILE.test(file) ? { file } : { reason: "the server's answer named no file" };
}

/**
 * Posts a report to the dev server, giving up after a time so the panel never waits for ever on a server that hangs.
 *
 * @param fetcher How the request is sent: the page's own `fetch`.
 * @param endpoint The endpoint `config.json` names, a path on the page's own origin; an empty string, which a page
 *   with none has, and anything that is not such a path (another origin, a protocol-relative address, any backslash,
 *   which a browser reads as a slash) post nothing.
 * @param submission The envelope and the report's text.
 * @param timeoutMs How long the request and its answer may take; the request is aborted then.
 * @returns The saved file's name, or the reason nothing was saved; it never rejects.
 */
export async function postBenchReport(fetcher: (input: string, init?: RequestInit) => Promise<Response>, endpoint: string, submission: BenchSubmission, timeoutMs: number = POST_TIMEOUT_MS): Promise<SaveOutcome> {
  if (!endpoint.startsWith("/") || endpoint.startsWith("//") || endpoint.includes("\\")) {
    return { reason: "this page has no dev server to save to" };
  }
  const body = JSON.stringify(submission);
  if (new TextEncoder().encode(body).byteLength > BENCH_SUBMISSION_MAX_BYTES) {
    return { reason: `the report is over ${BENCH_SUBMISSION_MAX_BYTES / 1024} KB` };
  }
  const timeout = abortAfter(timeoutMs);
  const late: SaveOutcome = { reason: `the dev server did not answer within ${timeoutMs / 1000} s` };
  const answer = (async (): Promise<SaveOutcome> => {
    try {
      return await readAnswer(await fetcher(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body, signal: timeout.signal }));
    } catch (error) {
      return timeout.signal.aborted ? late : { reason: `the dev server did not answer (${oneLine(error instanceof Error ? error.message : String(error))})` };
    }
  })();
  try {
    return await Promise.race([answer, timeout.expired.then(() => late)]);
  } finally {
    timeout.clear();
  }
}

/**
 * The panel's line about a finished post.
 *
 * @param outcome How the post ended.
 * @param label What the panel's label box holds.
 * @returns "Saved as" with the file's name and what became of the label, or "Not saved:" with the reason.
 */
export function savedLine(outcome: SaveOutcome, label: string): string {
  if ("reason" in outcome) {
    return `Not saved: ${outcome.reason}`;
  }
  const kept = benchLabel(label);
  const note = kept !== null ? `label ${kept}` : label === "" ? "no label; add &label=<name> to the link" : "the label is not in the file's name: use letters, digits, dash and underscore, at most 32";
  return `Saved as ${outcome.file} (${note})`;
}
